import React from 'react';

import ReactJson, {
  CollapsedFieldProps,
  OnCopyProps,
  OnSelectProps,
  onToggleCollapseProps,
} from '@dmitri-zganiaiko/react-json-view';
import { Colorize, ContentCopy } from '@mui/icons-material';
import { Fade, Stack, Tooltip } from '@mui/material';

import { Analytics } from '@/app/analytics/Analytics';
import { AnalyticsEvent } from '@/app/analytics/AnalyticsEvent';
import {
  ArrayNode,
  ContainerNode,
  getKeyAndValueForValueNode,
  getKeyIfNotToValueNode,
  HighlightableJson,
  Node,
  NodeType,
  ObjectChildLink,
  ObjectNode,
  ValueNode,
} from '@/app/log-viewer/highlighting/HighlightableJson';
import {
  HighlightableStringExpandableContainerState,
  isHighlightableStringExpandableContainer,
  toggleExpandingStateManually,
} from '@/app/log-viewer/highlighting/HighlightableStringExpandableContainer';
import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';

export interface JsonViewerProps {
  highlightableJson: DeepReadonly<HighlightableJson>;

  onDataChange: (change: DeepPartial<HighlightableJson>) => void;
  onCopyJqPath: (jqPath: string) => void;
  onExtractNewColumn: (jqPath: string) => void;
}

export class JsonViewer extends React.Component<JsonViewerProps> {
  render() {
    const hjson = this.props.highlightableJson;

    return (
      <div>
        {hjson.rootNode.type !== NodeType.Value ? (
          <ReactJson
            src={hjson.originalObject}
            name={null}
            style={{ fontFamily: 'var(--monospace-font-family)' }}
            displayDataTypes={false}
            sortKeys
            shouldCollapse={(item: CollapsedFieldProps) => {
              const node = this.convertNamespaceToNodeAndDiff(item.namespace)?.node;
              if (typeof node === 'undefined') {
                console.log(`Cannot find node for key`, item.namespace);
                return false;
              }

              if (isHighlightableStringExpandableContainer(node)) {
                const container = node as ContainerNode;
                return (
                  container.expandingState === HighlightableStringExpandableContainerState.Collapsed
                );
              }
              return false;
            }}
            onToggleCollapse={(item: onToggleCollapseProps) => {
              const nodeAndDiff = this.convertNamespaceToNodeAndDiff(item.namespace);
              if (typeof nodeAndDiff === 'undefined') {
                console.log(`Cannot find node for key`, item.namespace);
                return;
              }

              if (isHighlightableStringExpandableContainer(nodeAndDiff.node)) {
                const container = nodeAndDiff.node as ContainerNode;
                container.expandingState = toggleExpandingStateManually(container.expandingState);

                const diff = nodeAndDiff.diff.leafNodeDiff as DeepPartial<ContainerNode>;
                diff.expandingState = container.expandingState;

                this.props.onDataChange({
                  rootNode: nodeAndDiff.diff.rootNodeDiff,
                });
              }
            }}
            enableClipboard={false}
            keyRenderer={(item: OnSelectProps) => {
              const node = this.convertNamespaceToObjectChildLink(item.namespace);
              if (typeof node === 'undefined') {
                console.log(`Cannot find node for key`, item.namespace);
                // @ts-expect-error -- TODO: fix type
                return <span>{item.value}</span>;
              }

              const str = getKeyIfNotToValueNode(node.link, node.key);
              if (typeof str !== 'undefined') {
                return <HighlightableStringView string={str} />;
              }
              return null;
            }}
            scalarKeyValueRenderer={(item: OnSelectProps) => {
              const convertion = this.convertNamespaceToNodeAndDiff(item.namespace);
              const node = convertion?.node as ValueNode;
              if (typeof convertion === 'undefined' || typeof node === 'undefined') {
                console.log(`Cannot find node for value`, item);
                // @ts-expect-error -- TODO: fix type
                return <span>{item.value}</span>;
              }

              const str = getKeyAndValueForValueNode(node, convertion.key, convertion.value);

              return <HighlightableStringView string={str} />;
            }}
            additionalItemActions={(item: OnCopyProps) => {
              return (
                <AdditionalActions
                  bodyJson={hjson.originalObject}
                  namespace={item.namespace}
                  onCopyJqPath={() => {
                    const jqPath = this.composeJqQueryFromNamespace(item.namespace);
                    this.props.onCopyJqPath(jqPath);

                    Analytics.log(AnalyticsEvent.JsonCopyJqPath);
                  }}
                  onCopyJsonValue={() => {
                    let o = hjson.originalObject;
                    item.namespace.forEach((name) => {
                      if (name === null) {
                        return;
                      }
                      if (Array.isArray(o)) {
                        const index = parseInt(name);
                        o = o[index];
                      } else {
                        o = o[name];
                      }
                    });
                    const json = JSON.stringify(o, null, 4);
                    navigator.clipboard.writeText(json);

                    Analytics.log(AnalyticsEvent.JsonCopyJsonValue);
                  }}
                  onExtractNewColumn={() => {
                    const jqPath = this.composeJqQueryFromNamespace(item.namespace);
                    this.props.onExtractNewColumn(jqPath);

                    Analytics.log(AnalyticsEvent.JsonExtractJqColumn);
                  }}
                />
              );
            }}
          />
        ) : (
          // ReactJson can't render scalars like numbers, booleans etc.
          <div className="verbatim-multiline">
            <HighlightableStringView
              string={getKeyAndValueForValueNode(
                hjson.rootNode as ValueNode,
                undefined,
                hjson.originalObject,
              )}
            />
          </div>
        )}
      </div>
    );
  }

  private composeJqQueryFromNamespace(namespace: (string | null)[]): string {
    let o = this.props.highlightableJson.originalObject;
    let jqPath = '';
    namespace.forEach((name) => {
      if (name === null) {
        return;
      }

      if (Array.isArray(o)) {
        const index = parseInt(name);
        o = o[index];
        jqPath += `[${index}]`;
      } else {
        o = o[name];
        jqPath += `.${name}`;
      }
    });
    return jqPath;
  }

  private convertNamespaceToNodeAndDiff(namespace: (string | null)[]):
    | {
        node: Node;
        diff: {
          rootNodeDiff: DeepPartial<Node>;
          leafNodeDiff: DeepPartial<Node>;
        };
        key: string | undefined;
        value: any;
      }
    | undefined {
    let node: Node = this.props.highlightableJson.rootNode;
    let key: string | undefined;
    let value: any = this.props.highlightableJson.originalObject;

    const rootNodeDiff: DeepPartial<Node> = {};
    let diff: {
      rootNodeDiff: DeepPartial<Node>;
      leafNodeDiff: DeepPartial<Node>;
    } = {
      rootNodeDiff,
      leafNodeDiff: rootNodeDiff,
    };

    for (const n of namespace) {
      if (n === null) {
        // Skip.
        continue;
      }

      switch (node.type) {
        case NodeType.Value:
          console.error('Cannot go deeper', n, namespace, node);
          return undefined;
        case NodeType.Object:
          const objectNode = node as ObjectNode;
          const link = objectNode.children.get(n);
          if (typeof link === 'undefined') {
            console.error('Cannot go deeper', n, namespace, node);
            return undefined;
          }

          node = link.child;
          key = n;
          value = value[n];

          const newLeafNodeDiffInObject: DeepPartial<Node> = {};
          if (typeof diff === 'undefined') {
            diff = {
              leafNodeDiff: newLeafNodeDiffInObject,
              rootNodeDiff: newLeafNodeDiffInObject,
            };
          } else {
            const objectNodeDiff = diff.leafNodeDiff as DeepPartial<ObjectNode>;

            const diffChildren = new Map<string, DeepPartial<ObjectChildLink>>();
            diffChildren.set(n, {
              child: newLeafNodeDiffInObject,
            });

            objectNodeDiff.children = diffChildren;
            diff.leafNodeDiff = newLeafNodeDiffInObject;
          }

          break;
        case NodeType.Array:
          const index = parseInt(n);
          const arrayNode = node as ArrayNode;
          const child = arrayNode.children[index];
          node = child;
          key = undefined;
          value = value[index];

          const newLeafDiffNodeInArray: DeepPartial<Node> = {};
          if (typeof diff === 'undefined') {
            diff = {
              leafNodeDiff: newLeafDiffNodeInArray,
              rootNodeDiff: newLeafDiffNodeInArray,
            };
          } else {
            const arrayNodeDiff = diff.leafNodeDiff as DeepPartial<ArrayNode>;
            arrayNodeDiff.children = new Array(arrayNode.children.length);
            arrayNodeDiff.children[index] = newLeafDiffNodeInArray;

            diff.leafNodeDiff = newLeafDiffNodeInArray;
          }

          break;
        default:
          console.error('Unknown node type', node, n, namespace);
          break;
      }
    }

    return {
      node,
      diff,
      key,
      value,
    };
  }

  private convertNamespaceToObjectChildLink(namespace: (string | null)[]):
    | {
        link: ObjectChildLink;
        key: string | undefined;
      }
    | undefined {
    let node: Node = this.props.highlightableJson.rootNode;
    let key: string | undefined;

    for (let i = 0; i < namespace.length; ++i) {
      const n = namespace[i];
      if (n === null) {
        // Skip.
        continue;
      }
      const isLast = i === namespace.length - 1;

      switch (node.type) {
        case NodeType.Value:
          console.error('Cannot go deeper', n, namespace, node);
          return undefined;
        case NodeType.Object:
          const objectNode = node as ObjectNode;
          const link = objectNode.children.get(n);
          key = n;
          if (typeof link === 'undefined') {
            console.error('Cannot go deeper', n, namespace, node);
            return undefined;
          }

          if (isLast) {
            return { link, key };
          }
          node = link.child;

          break;
        case NodeType.Array:
          const index = parseInt(n);
          const arrayNode = node as ArrayNode;
          const child = arrayNode.children[index];
          node = child;
          key = undefined;
          break;
      }
    }

    return undefined;
  }
}

type AdditionalActionsProps = {
  bodyJson: Record<string, any>;
  namespace: (string | null)[];

  onCopyJsonValue: () => void;
  onCopyJqPath: () => void;
  onExtractNewColumn: () => void;
};

class AdditionalActions extends React.Component<AdditionalActionsProps> {
  render() {
    return (
      <div className="json-additional-actions">
        <Stack
          direction="row"
          alignItems="center"
          spacing="16px"
          className="json-additional-actions-buttons"
        >
          <Tooltip disableInteractive TransitionComponent={Fade} title="Copy JSON value">
            <ContentCopy
              className="icon"
              onClick={() => {
                this.props.onCopyJsonValue();
              }}
            />
          </Tooltip>

          <Tooltip disableInteractive TransitionComponent={Fade} title="Copy jq path">
            <span
              className="text"
              onClick={() => {
                this.props.onCopyJqPath();
              }}
            >
              jq
            </span>
          </Tooltip>

          <Tooltip disableInteractive TransitionComponent={Fade} title="Display in new column">
            <Colorize
              className="icon"
              onClick={() => {
                this.props.onExtractNewColumn();
              }}
            />
          </Tooltip>
        </Stack>
      </div>
    );
  }
}
